import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type SubscriptionTier = "solo" | "professional" | "enterprise";
export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled";

export type Subscription = {
  id: string;
  user_id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  trial_ends_at: string | null;
  current_period_start: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  notes: string | null;
};

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  solo: "Solo",
  professional: "Professional",
  enterprise: "Enterprise",
};

/** Hard caps, enforced by generate-document. null = no hard cap. */
export const TIER_DRAFT_LIMITS: Record<SubscriptionTier, number | null> = {
  solo: 50,
  professional: null, // unlimited under fair use
  enterprise: null,
};

/** Fair-use soft caps (docs/02-architecture.md), enforced by generate-document
 * with a distinct FAIR_USE_EXCEEDED code. null = none. */
export const TIER_FAIR_USE_LIMITS: Record<SubscriptionTier, number | null> = {
  solo: null, // solo has a hard cap instead
  professional: 300,
  enterprise: null,
};

/**
 * Loads the current user's subscription row, the tier the system actually
 * grants them right now (handles trial expiry), and how many drafts they've
 * used this month.
 */
export const useSubscription = () => {
  const { user } = useAuth();

  const subQuery = useQuery({
    queryKey: ["subscription", user?.id ?? "anon"],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Subscription | null;
    },
  });

  const effectiveTierQuery = useQuery({
    queryKey: ["effective-tier", user?.id ?? "anon"],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      if (!user) return "solo" as SubscriptionTier;
      const { data, error } = await supabase.rpc("effective_tier", { _user_id: user.id });
      if (error) throw error;
      return (data ?? "solo") as SubscriptionTier;
    },
  });

  const usageQuery = useQuery({
    queryKey: ["monthly-draft-count", user?.id ?? "anon"],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      if (!user) return 0;
      const { data, error } = await supabase.rpc("monthly_draft_count", { _user_id: user.id });
      if (error) throw error;
      return (data as number) ?? 0;
    },
  });

  const subscription = subQuery.data ?? null;
  const effectiveTier = effectiveTierQuery.data ?? "solo";
  const draftsThisMonth = usageQuery.data ?? 0;
  const draftLimit = TIER_DRAFT_LIMITS[effectiveTier];
  const fairUseLimit = TIER_FAIR_USE_LIMITS[effectiveTier];

  // Trial countdown (days remaining, 0 if expired/none).
  let trialDaysLeft = 0;
  if (subscription?.status === "trialing" && subscription.trial_ends_at) {
    const ms = new Date(subscription.trial_ends_at).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
  }

  return {
    subscription,
    effectiveTier,
    tierLabel: TIER_LABELS[effectiveTier],
    isTrialing: subscription?.status === "trialing" && trialDaysLeft > 0,
    trialDaysLeft,
    draftsThisMonth,
    draftLimit,
    fairUseLimit,
    isOverLimit: draftLimit !== null && draftsThisMonth >= draftLimit,
    isOverFairUse: fairUseLimit !== null && draftsThisMonth >= fairUseLimit,
    isLoading: subQuery.isLoading || effectiveTierQuery.isLoading,
  };
};
